import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertCircle, MapPin } from "lucide-react";
import DirectionsLink from "@/components/booking/DirectionsLink";
import VendorNameLink from "@/components/common/VendorNameLink";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling, paid, failed
  const [booking, setBooking] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let attempts = 0;
    const max = 8;
    const poll = async () => {
      try {
        const { data } = await api.get(`/bookings/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setBooking(data.booking);
          // Iter 89 — paid but awaiting host approval: show the pending
          // message, NOT a confirmation.
          setStatus(data.booking?.status === "pending_approval" ? "pending_approval" : "paid");
          return;
        }
        if (data.status === "expired") { setStatus("failed"); return; }
        attempts++;
        if (attempts >= max) { setStatus("failed"); return; }
        setTimeout(poll, 2500);
      } catch {
        attempts++;
        if (attempts >= max) { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div>
      <Header />
      <div className="max-w-md mx-auto px-4 py-24 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-blue-600 animate-spin mb-6" />
            <h1 className="font-display text-2xl font-medium mb-2">Confirming your booking…</h1>
            <p className="text-sm text-neutral-500">Please don't close this page.</p>
          </>
        )}
        {status === "pending_approval" && (
          <>
            <Loader2 className="h-14 w-14 mx-auto text-amber-500 mb-6" />
            <h1 className="font-display text-3xl font-medium mb-3" data-testid="booking-pending-approval-title">Request sent to the host</h1>
            <p className="text-neutral-600 mb-6" data-testid="booking-pending-approval-message">
              Your reservation request has been sent to the rental company. The vehicle is temporarily
              reserved for you while the host reviews your request. You will receive confirmation by
              email as soon as the host responds.
            </p>
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/dashboard/customer" data-testid="booking-pending-view-dashboard">View my bookings</Link>
            </Button>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-green-600 mb-6" />
            <h1 className="font-display text-3xl font-medium mb-3" data-testid="booking-success-title">Booking confirmed!</h1>
            <p className="text-neutral-600 mb-6">Your trip is reserved. We've sent the details to your email.</p>
            {booking?.vendor && (
              <div
                className="text-sm text-neutral-700 -mt-4 mb-6"
                data-testid="booking-success-host"
              >
                Booked with{" "}
                <VendorNameLink
                  vendor={booking.vendor}
                  className="text-blue-600 hover:underline font-medium"
                  testIdSuffix="booking-success"
                />
              </div>
            )}
            {booking?.pickup_location && (
              <div
                className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-teal-50/40 p-4 mb-6 text-left"
                data-testid="booking-success-pickup-card"
              >
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-xl bg-emerald-100 ring-1 ring-emerald-200 flex items-center justify-center shrink-0">
                    <MapPin className="h-4 w-4 text-emerald-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-0.5">Pick up at</div>
                    <div className="text-sm font-medium text-neutral-900 truncate">{booking.pickup_location}</div>
                    {booking.pickup_time && (
                      <div className="text-[11px] text-neutral-500 mt-0.5">
                        {booking.start_date?.slice(0, 10)} · {booking.pickup_time}
                      </div>
                    )}
                    <div className="mt-2">
                      <DirectionsLink location={booking.pickup_location} testId="booking-success-directions" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate("/dashboard/customer")} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="view-bookings-btn">View my bookings</Button>
              <Link to="/cars"><Button variant="ghost" className="rounded-full">Browse more</Button></Link>
            </div>

            {/* Iter 67eb — Post-payment reassurance: reminds the renter what's
                covered and how to reach us, easing buyer's remorse. */}
            <div
              className="mt-8 grid grid-cols-2 gap-2 text-left"
              data-testid="booking-success-trust-row"
            >
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-0.5">Free cancellation</div>
                <div className="text-[11px] text-neutral-600 leading-snug">Up to 24h before pickup, no questions asked.</div>
              </div>
              <div className="rounded-xl border border-sky-100 bg-sky-50/40 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-sky-700 mb-0.5">24/7 support</div>
                <div className="text-[11px] text-neutral-600 leading-snug">We answer in EN, SQ and IT — anytime.</div>
              </div>
              <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700 mb-0.5">Digital agreement</div>
                <div className="text-[11px] text-neutral-600 leading-snug">Sign on your phone at pickup — no paper.</div>
              </div>
              <div className="rounded-xl border border-amber-100 bg-amber-50/40 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-700 mb-0.5">PDF invoice</div>
                <div className="text-[11px] text-neutral-600 leading-snug">In your inbox right after pickup.</div>
              </div>
            </div>
          </>
        )}
        {status === "failed" && (
          <>
            <AlertCircle className="h-12 w-12 mx-auto text-red-600 mb-6" />
            <h1 className="font-display text-2xl font-medium mb-2">Payment not confirmed</h1>
            <p className="text-sm text-neutral-500 mb-6">We couldn't confirm your payment. Please try again.</p>
            <Link to="/cars"><Button className="rounded-full bg-blue-600 hover:bg-blue-700">Back to browse</Button></Link>
          </>
        )}
      </div>
      {/* Iter 67em — chat bubble now mounted globally in App.js. */}
    </div>
  );
}
